import { publicApi, privateApi } from '../config/api';

class CurrencyService {
  constructor() {
    this.coinsCache = null;
    this.pairsCache = null;
    this.networkFeesCache = {};
    this.cacheExpiry = 5 * 60 * 1000; // 5 minutes
    this.lastFetch = null;
  }

  // Check if cache is valid
  isCacheValid() {
    return this.lastFetch && (Date.now() - this.lastFetch) < this.cacheExpiry;
  }

  // Fetch all coins data from backend
  async getCoinsData(forceRefresh = false) {
    if (this.coinsCache && this.isCacheValid() && !forceRefresh) {
      return this.coinsCache;
    }

    try {
      const response = await publicApi.get('/v1/coins/');
      this.coinsCache = response.data;
      this.lastFetch = Date.now();
      return this.coinsCache;
    } catch (error) {
      console.error('Error fetching coins data:', error);
      throw error;
    }
  }

  // Fetch pairs data from backend
  async getPairsData(forceRefresh = false) {
    if (this.pairsCache && this.isCacheValid() && !forceRefresh) {
      return this.pairsCache;
    }

    try {
      const response = await publicApi.get('/v1/pairs/');
      this.pairsCache = response.data;
      return this.pairsCache;
    } catch (error) {
      console.error('Error fetching pairs data:', error);
      throw error;
    }
  }

  // Get all available currencies for deposits/withdrawals
  async getAvailableCurrencies() {
    try {
      const coinsData = await this.getCoinsData();
      const coins = coinsData.coins || {};
      
      return Object.keys(coins)
        .filter(coinCode => {
          const coin = coins[coinCode];
          // Only include coins that are not disabled for deposits/withdrawals
          return !coin.disable_topups && !coin.disable_withdrawals && !coin.disable_all;
        })
        .map(coinCode => {
          const coin = coins[coinCode];
          return {
            code: coinCode,
            name: coin.name,
            decimals: coin.decimals,
            isToken: coin.is_token,
            blockchainList: coin.blockchain_list || [],
            logo: coin.logo,
            txExplorer: coin.tx_explorer,
            disableTopups: coin.disable_topups,
            disableWithdrawals: coin.disable_withdrawals,
            price: coin.price || 0
          };
        })
        .sort((a, b) => a.name.localeCompare(b.name));
    } catch (error) {
      console.error('Error getting available currencies:', error);
      return [];
    }
  }

  // Get available networks for a specific currency
  async getNetworksForCurrency(currencyCode) {
    try {
      const coinsData = await this.getCoinsData();
      const coins = coinsData.coins || {};
      const blockchainDefinitions = coinsData.commons?.blockchain_definitions || {};
      
      const currency = coins[currencyCode];
      if (!currency) {
        throw new Error(`Currency ${currencyCode} not found`);
      }

      // If it's a token, return its blockchain networks
      if (currency.is_token && currency.blockchain_list?.length > 0) {
        return currency.blockchain_list.map(blockchainCode => ({
          code: blockchainCode,
          name: this.getNetworkDisplayName(blockchainCode, blockchainDefinitions),
          symbol: blockchainCode,
          type: blockchainDefinitions[blockchainCode] || blockchainCode
        }));
      }

      // If it's a native blockchain currency, return itself
      if (!currency.is_token) {
        return [{
          code: currencyCode,
          //name: currency.name,
          name: this.getNetworkDisplayName(currencyCode, blockchainDefinitions),
          symbol: currencyCode,
          type: 'Native'
        }];
      }

      return [];
    } catch (error) {
      console.error(`Error getting networks for ${currencyCode}:`, error);
      return [];
    }
  }

  // Get display name for network
  getNetworkDisplayName(blockchainCode, blockchainDefinitions) {
    const networkNames = {
      'ETH': 'Ethereum (ERC-20)',
      'BNB': 'BNB Smart Chain (BEP-20)', 
      'TRX': 'Tron (TRC-20)',
      'MATIC': 'Polygon',
      'BTC': 'Bitcoin',
      'BTC1': 'BTC1'
    };

    return networkNames[blockchainCode] || `${blockchainCode} Network`;
  }

  // Get fees and limits for a currency and network
  async getFeesAndLimits(currencyCode, networkCode = null) {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        console.warn('No access token available for fees API');
        return null;
      }

      // Get comprehensive limits from /v1/limits/ API
      let limitsData = null;
      try {
        const limitsResponse = await privateApi.get(`/v1/limits/`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        limitsData = limitsResponse.data[currencyCode];
      } catch (error) {
        console.warn(`Could not fetch limits for ${currencyCode}:`, error);
      }

      // Try to get withdrawal fees (legacy API)
      let withdrawalFee = null;
      try {
        const withdrawalResponse = await privateApi.get(`/v1/withdrawalfees/${currencyCode}/`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        withdrawalFee = withdrawalResponse.data[currencyCode];
      } catch (error) {
        console.warn(`Could not fetch withdrawal fees for ${currencyCode}:`, error);
      }

      return {
        withdrawalFee,
        limitsData, // This contains all the comprehensive limits and fees
        networkCode,
        currencyCode
      };
    } catch (error) {
      console.error(`Error getting fees and limits for ${currencyCode}:`, error);
      return null;
    }
  }

  // Get network-specific withdrawal fee
  getNetworkWithdrawalFee(feesData, networkCode, currencyCode) {
    // Try the new limitsData first
    if (feesData?.limitsData?.fee?.withdrawal?.address) {
      const withdrawalFees = feesData.limitsData.fee.withdrawal.address;
      
      // For tokens, map network to blockchain currency
      if (networkCode && networkCode !== currencyCode) {
        return withdrawalFees[networkCode];
      }
      
      // For native currencies
      return withdrawalFees[currencyCode];
    }
    
    // Fallback to old structure
    if (feesData?.depositLimits?.fee?.withdrawal?.address) {
      const withdrawalFees = feesData.depositLimits.fee.withdrawal.address;
      
      if (networkCode && networkCode !== currencyCode) {
        return withdrawalFees[networkCode];
      }
      
      return withdrawalFees[currencyCode];
    }
    
    return null;
  }

  // Format fee for display
  formatFee(fee) {
    if (fee === null || fee === undefined) return '0';

    const num = parseFloat(fee);
    if (isNaN(num)) return '0';

    // Format to show appropriate decimal places
    if (num < 0.0001) {
      return num.toFixed(8);
    } else if (num < 1) {
      return num.toFixed(6);
    } else {
      return num.toFixed(4);
    }
  }

  // Get min/max deposit amounts
  getDepositLimits(feesData) {
    // Try the new limitsData first
    if (feesData?.limitsData?.limits?.deposit) {
      const limits = feesData.limitsData.limits.deposit;
      return {
        min: limits.min || '0',
        max: limits.max || 'No limit'
      };
    }
    
    // Fallback to old structure
    if (feesData?.depositLimits?.limits?.deposit) {
      const limits = feesData.depositLimits.limits.deposit;
      return {
        min: limits.min || '0',
        max: limits.max || 'No limit'
      };
    }
    
    return { min: '0', max: 'No limit' };
  }

  // Get min/max withdrawal amounts 
  getWithdrawalLimits(feesData) {
    // Try the new limitsData first
    if (feesData?.limitsData?.limits?.withdrawal) {
      const limits = feesData.limitsData.limits.withdrawal;
      return {
        min: limits.min || '0',
        max: limits.max || 'No limit'
      };
    }
    
    // Fallback to old structure
    if (feesData?.depositLimits?.limits?.withdrawal) {
      const limits = feesData.depositLimits.limits.withdrawal;
      return {
        min: limits.min || '0',
        max: limits.max || 'No limit'
      };
    }
    
    return { min: '0', max: 'No limit' };
  }

  // Get comprehensive fee information
  getAllFees(feesData) {
    if (!feesData?.limitsData?.fee) {
      return null;
    }

    const fees = feesData.limitsData.fee;
    return {
      deposit: {
        address: fees.deposit?.address || 0,
        code: fees.deposit?.code || 0
      },
      withdrawal: {
        address: fees.withdrawal?.address || {},
        code: fees.withdrawal?.code || 0
      },
      order: {
        limit: fees.order?.limit || 0,
        market: fees.order?.market || 0
      }
    };
  }

  // Get confirmations required for a network
  getNetworkConfirmations(networkCode) {
    const confirmations = {
      'BTC': 2,
      'BTC1': 2,
      'ETH': 12,
      'BNB': 15,
      'TRX': 19,
      'MATIC': 128
    };

    return confirmations[networkCode] || 6;
  }

  // Clear cache
  clearCache() {
    this.coinsCache = null;
    this.pairsCache = null;
    this.networkFeesCache = {};
    this.lastFetch = null;
  }

  // Get all supported trading pairs
  async getTradingPairs() {
    try {
      const pairsData = await this.getPairsData();
      return pairsData.pairs || [];
    } catch (error) {
      console.error('Error getting trading pairs:', error);
      return [];
    }
  }

  // Check if a currency supports deposits
  async supportsDeposits(currencyCode) {
    try {
      const coinsData = await this.getCoinsData();
      const coin = coinsData.coins?.[currencyCode];
      return coin && !coin.disable_topups && !coin.disable_all;
    } catch (error) {
      console.error(`Error checking deposit support for ${currencyCode}:`, error);
      return false;
    }
  }

  // Check if a currency supports withdrawals
  async supportsWithdrawals(currencyCode) {
    try {
      const coinsData = await this.getCoinsData();
      const coin = coinsData.coins?.[currencyCode];
      return coin && !coin.disable_withdrawals && !coin.disable_all;
    } catch (error) {
      console.error(`Error checking withdrawal support for ${currencyCode}:`, error);
      return false;
    }
  }
}

export default new CurrencyService();
